import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import {
  BookOpen, Play, CheckCircle, Lock, CaretRight, House,
  Crown, Lightning, Target, Trophy, ArrowRight, Clock,
  SignOut, Gear, Cross, GraduationCap, Sparkle
} from '@phosphor-icons/react';
import { getCourseImage } from '../config/courseImages';

const API = process.env.REACT_APP_BACKEND_URL;

const CoursesPage = () => {
  const { user, logout, isPaidMember, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [courseData, setCourseData] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCourse, setActiveCourse] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/api/courses`, { headers: getAuthHeader() });
      setCourses(res.data);
      
      const dataPromises = res.data.map(async (course) => {
        try {
          const [modulesRes, progressRes] = await Promise.all([
            axios.get(`${API}/api/courses/${course.id}/modules`, { headers: getAuthHeader() }),
            axios.get(`${API}/api/courses/${course.id}/progress`, { headers: getAuthHeader() }).catch(() => ({ data: { completed_modules: [] } }))
          ]);
          return {
            id: course.id,
            modules: modulesRes.data,
            progress: progressRes.data?.completed_modules || []
          };
        } catch (err) {
          return { id: course.id, modules: [], progress: [] };
        }
      });
      
      const allData = await Promise.all(dataPromises);
      const dataMap = {};
      allData.forEach(d => { dataMap[d.id] = d; });
      setCourseData(dataMap);
      
      if (res.data.length > 0) {
        setActiveCourse(res.data[0].id);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getCourseIcon = (courseId) => {
    switch (courseId) {
      case 'powr': return Target;
      case 'ai-income': return Lightning;
      case 'inner-circle': return Crown;
      case 'affiliateai': return Trophy;
      default: return BookOpen;
    }
  };

  const currentCourse = courses.find(c => c.id === activeCourse);
  const currentData = courseData[activeCourse];
  const modules = currentData?.modules || [];
  const completedModules = currentData?.progress || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-pulse text-[#C4A661]">Loading courses...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header - Dark */}
      <nav className="nav-glass">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to="/community" className="text-2xl font-extrabold tracking-tighter text-[#C4A661]">POWR</Link>
          
          <div className="flex items-center gap-6">
            <Link to="/community" className="text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors flex items-center gap-2 text-sm uppercase tracking-widest">
              <House size={18} />
              <span className="hidden md:inline">Community</span>
            </Link>
            <button 
              onClick={handleLogout}
              className="text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors"
              title="Sign out"
            >
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Light */}
      <section className="bg-[#FDFDFD] border-b border-[#E5E5E5]">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-overline mb-4">Training</p>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-[#0A0A0A] mb-3">
              Kingdom <span className="text-[#C4A661]">Training</span>
            </h1>
            <p className="text-[#525252] text-lg max-w-xl">
              Build your knowledge. <span className="font-editorial italic text-[#0A0A0A]">Transform your life.</span> 
              Faith-driven courses for wealth and purpose.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Scripture Banner */}
      <section className="bg-[#C4A661] py-4">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <p className="text-[#0A0A0A] font-medium text-sm">
            <Cross size={16} className="inline-block mr-2 -mt-1" weight="bold" />
            "Study to show thyself approved unto God, a workman that needeth not to be ashamed." — 2 Timothy 2:15
          </p>
        </div>
      </section>

      {/* Main Content - Dark */}
      <div className="container mx-auto max-w-6xl px-6 md:px-12 lg:px-24 py-12">
        <div className="grid lg:grid-cols-[300px_1fr] gap-10">
          
          {/* Course List Sidebar */}
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-[#A3A3A3] uppercase tracking-[0.2em] mb-5 px-2 flex items-center gap-2">
              <Sparkle size={14} className="text-[#C4A661]" />
              Available Courses
            </h3>
            {courses.map((course) => {
              const Icon = getCourseIcon(course.id);
              const data = courseData[course.id];
              const progress = data?.progress?.length || 0;
              const total = data?.modules?.length || course.total_modules || 0;
              const isLocked = course.is_premium && !isPaidMember;
              const courseImg = getCourseImage(course.id);
              
              return (
                <button
                  key={course.id}
                  onClick={() => !isLocked && setActiveCourse(course.id)}
                  disabled={isLocked}
                  className={`w-full text-left border transition-all rounded-xl overflow-hidden group ${
                    activeCourse === course.id
                      ? 'border-[#C4A661] ring-2 ring-[#C4A661]/20'
                      : isLocked
                        ? 'bg-[#141414] border-[#262626] cursor-not-allowed opacity-60'
                        : 'bg-[#141414] border-[#262626] hover:border-[#C4A661]/50'
                  }`}
                  data-testid={`course-tab-${course.id}`}
                >
                  {/* Course Thumbnail */}
                  <div className="relative h-24 overflow-hidden">
                    <img 
                      src={courseImg.thumbnail}
                      alt={courseImg.alt}
                      className={`w-full h-full object-cover transition-transform ${!isLocked && 'group-hover:scale-105'}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                    {isLocked && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <Lock size={24} className="text-white/50" />
                      </div>
                    )}
                    {activeCourse === course.id && (
                      <div className="absolute top-2 right-2 bg-[#C4A661] text-[#0A0A0A] text-xs font-bold px-2 py-1 rounded-full">
                        Active
                      </div>
                    )}
                  </div>
                  
                  {/* Course Info */}
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg ${
                        activeCourse === course.id ? 'bg-[#C4A661]/20 border border-[#C4A661]/30' : 'bg-[#262626] border border-[#262626]'
                      }`}>
                        <Icon size={18} className={activeCourse === course.id ? 'text-[#C4A661]' : 'text-[#A3A3A3]'} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-sm truncate ${
                          activeCourse === course.id ? 'text-[#C4A661]' : 'text-[#F7F7F5]'
                        }`}>
                          {course.title}
                        </p>
                        <p className="text-xs mt-1 text-[#525252]">
                          {isLocked ? 'Inner Circle Only' : `${progress}/${total} modules`}
                        </p>
                        {!isLocked && total > 0 && (
                          <div className="mt-2 h-1.5 bg-[#262626] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#C4A661] rounded-full transition-all"
                              style={{ width: `${(progress / total) * 100}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
            
            {/* Upgrade CTA for non-paid */}
            {!isPaidMember && (
              <div className="bg-gradient-to-br from-[#141414] to-[#0A0A0A] border border-[#C4A661]/30 p-5 mt-6">
                <div className="flex items-center gap-2 mb-3">
                  <Crown size={16} className="text-[#C4A661]" weight="fill" />
                  <span className="text-[#C4A661] text-xs font-bold uppercase tracking-widest">Unlock All</span>
                </div>
                <p className="text-sm text-[#A3A3A3] mb-4">
                  Get access to premium courses with Inner Circle.
                </p>
                <Link to="/inner-circle-sales">
                  <Button className="w-full btn-gold text-sm h-10">
                    Join Inner Circle
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Course Content */}
          <div>
            <AnimatePresence mode="wait">
              {currentCourse && (
                <motion.div
                  key={activeCourse}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  {/* Course Header - with HD Image */}
                  <div className="relative overflow-hidden rounded-xl mb-8">
                    {/* Header Image */}
                    <div className="relative h-48 md:h-56">
                      <img 
                        src={getCourseImage(activeCourse).header}
                        alt={getCourseImage(activeCourse).alt}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
                    </div>
                    
                    {/* Course Info Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-[#C4A661]/20 border border-[#C4A661]/30 flex items-center justify-center rounded-xl backdrop-blur-sm">
                          {(() => { const Icon = getCourseIcon(activeCourse); return <Icon size={28} className="text-[#C4A661]" weight="fill" />; })()}
                        </div>
                        <div className="flex-1">
                          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">{currentCourse.title}</h2>
                          <p className="text-white/70 text-sm hidden md:block">{currentCourse.description}</p>
                          <div className="flex items-center gap-4 mt-3 text-sm text-white/60">
                            <span className="flex items-center gap-2">
                              <BookOpen size={16} />
                              {modules.length} modules
                            </span>
                            <span className="flex items-center gap-2">
                              <CheckCircle size={16} className="text-emerald-400" />
                              {completedModules.length} completed
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
                      <div 
                        className="h-full bg-[#C4A661] transition-all"
                        style={{ width: `${modules.length > 0 ? (completedModules.length / modules.length) * 100 : 0}%` }}
                      />
                    </div>
                  </div>

                  {/* Modules List */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xs font-bold text-[#A3A3A3] uppercase tracking-[0.2em] flex items-center gap-2">
                        <GraduationCap size={16} className="text-[#C4A661]" />
                        Course Modules
                      </h3>
                      <span className="text-xs text-[#525252]">{completedModules.length}/{modules.length} complete</span>
                    </div>
                    {modules.length === 0 ? (
                      <div className="bg-[#141414] border border-[#262626] p-10 text-center rounded-xl">
                        <BookOpen size={40} className="text-[#525252] mx-auto mb-4" />
                        <p className="text-[#A3A3A3]">No modules available yet</p>
                      </div>
                    ) : (
                      modules.map((module, index) => {
                        const isCompleted = completedModules.includes(module.slug);
                        const moduleLink = `/${activeCourse}/${module.slug}`;
                        
                        return (
                          <Link key={module.slug} to={moduleLink}>
                            <motion.div 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                              className={`p-5 border transition-all hover:-translate-y-1 rounded-xl group ${
                                isCompleted 
                                  ? 'bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border-emerald-500/30' 
                                  : 'bg-gradient-to-r from-[#141414] to-[#1A1A1A] border-[#262626] hover:border-[#C4A661]/50'
                              }`}
                            >
                              <div className="flex items-center gap-5">
                                <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-xl ${
                                  isCompleted 
                                    ? 'bg-emerald-500/20 text-emerald-400' 
                                    : 'bg-[#262626] text-[#A3A3A3] group-hover:bg-[#C4A661]/20 group-hover:text-[#C4A661]'
                                } transition-all`}>
                                  {isCompleted ? (
                                    <CheckCircle size={24} weight="fill" />
                                  ) : (
                                    <span className="font-bold">{index + 1}</span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`font-semibold text-base ${isCompleted ? 'text-emerald-400' : 'text-[#F7F7F5]'}`}>
                                    {module.title}
                                  </p>
                                  <p className="text-sm text-[#A3A3A3] truncate mt-1">
                                    {module.content?.identity_statement?.substring(0, 80) || 'Module content'}...
                                  </p>
                                </div>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  isCompleted ? 'bg-emerald-500/20' : 'bg-[#262626] group-hover:bg-[#C4A661]/20'
                                } transition-all`}>
                                  <CaretRight size={20} className={isCompleted ? 'text-emerald-400' : 'text-[#525252] group-hover:text-[#C4A661]'} />
                                </div>
                              </div>
                            </motion.div>
                          </Link>
                        );
                      })
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursesPage;
